/**
 * Call-recording player for the Call History detail page.
 *
 * Mirrors the legacy agent-portal player operators are used to (WaveSurfer):
 * the two parties on the left, a waveform per audio channel (split view —
 * the top lane is the customer side, the bottom lane the agent side), a time
 * ruler, "Total call time", and Play / Pause / Stop / Backward / Forward /
 * Download.
 *
 * The stream is fetched with credentials (RECORDINGS_VIEW is enforced by the
 * API) and handed to WaveSurfer as a Blob — no auth-bearing URL ever reaches
 * the <audio> element. Mono recordings are mirrored onto both lanes so the
 * split view is always there (a mono telephone recording mixes both parties
 * anyway). PCI pause segments are skipped client-side.
 */
import {
  forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState, type JSX,
} from 'react'
import WaveSurfer from 'wavesurfer.js'
import TimelinePlugin from 'wavesurfer.js/dist/plugins/timeline.js'
import {
  AlertCircle, Download, FastForward, Loader2, Pause, Play, Rewind, Square, UserRound,
} from 'lucide-react'

export interface CallRecordingPlayerHandle {
  /** Seek to `seconds` and start playing — used by transcript lines. */
  playFrom: (seconds: number) => void
}

export interface CallRecordingPlayerProps {
  recordingId: string
  source: string
  destination: string
  /** Labels for the two lanes (top = Customer, bottom = Agent by default). */
  channelLabels?: [string, string]
  fileName: string
  onTimeUpdate?: (seconds: number) => void
}

interface PciPauseSegment {
  startOffsetMs: number
  endOffsetMs: number
}

const SKIP_SECONDS = 10

/** Lane colors — lane 0 (Customer) emerald, lane 1 (Agent) blue, like the
 *  transcript's speaker colors. */
const LANE_COLORS = [
  { waveColor: '#6ee7b7', progressColor: '#059669', chip: 'bg-emerald-100/90 text-emerald-700' },
  { waveColor: '#93c5fd', progressColor: '#2563eb', chip: 'bg-blue-100/90 text-blue-700' },
]

/** `hh:mm:ss`, as the legacy player printed it. */
export function hhmmss(totalSec: number): string {
  const s = Number.isFinite(totalSec) && totalSec > 0 ? Math.floor(totalSec) : 0
  const pad = (n: number): string => String(n).padStart(2, '0')
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`
}

function Party({ label, number, tone }: { label: string; number: string; tone: 'blue' | 'emerald' }): JSX.Element {
  return (
    <div className="flex flex-col items-center text-center">
      <span className={`flex h-10 w-10 items-center justify-center rounded-full ring-4 ${
        tone === 'blue' ? 'bg-blue-50 text-blue-600 ring-blue-50/60' : 'bg-emerald-50 text-emerald-600 ring-emerald-50/60'
      }`}>
        <UserRound className="h-5 w-5" />
      </span>
      <span className="mt-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</span>
      <span className="max-w-[9rem] truncate text-sm font-semibold text-slate-800" title={number}>{number || '—'}</span>
    </div>
  )
}

export const CallRecordingPlayer = forwardRef<CallRecordingPlayerHandle, CallRecordingPlayerProps>(
  function CallRecordingPlayer({ recordingId, source, destination, channelLabels, fileName, onTimeUpdate }, ref) {
    const waveRef = useRef<HTMLDivElement | null>(null)
    const timelineRef = useRef<HTMLDivElement | null>(null)
    const wsRef = useRef<WaveSurfer | null>(null)
    const [url, setUrl] = useState<string | null>(null)
    const [lanes, setLanes] = useState(0)
    const [duration, setDuration] = useState(0)
    const [current, setCurrent] = useState(0)
    const [playing, setPlaying] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [segments, setSegments] = useState<PciPauseSegment[]>([])

    // Build the WaveSurfer instance once.
    useEffect(() => {
      if (!waveRef.current || !timelineRef.current) return
      const ws = WaveSurfer.create({
        container: waveRef.current,
        height: 64,
        splitChannels: LANE_COLORS,
        normalize: true,
        cursorColor: '#334155',
        cursorWidth: 2,
        backend: 'MediaElement',
        plugins: [
          TimelinePlugin.create({ container: timelineRef.current }),
        ],
      })
      wsRef.current = ws
      const offTime = ws.on('timeupdate', (t) => {
        const ms = t * 1000
        const seg = segmentsRef.current.find((s) => ms >= s.startOffsetMs && ms < s.endOffsetMs)
        if (seg) { ws.setTime(seg.endOffsetMs / 1000); return }
        setCurrent(t)
        onTimeUpdateRef.current?.(t)
      })
      const offPlay = ws.on('play', () => setPlaying(true))
      const offPause = ws.on('pause', () => setPlaying(false))
      const offFinish = ws.on('finish', () => setPlaying(false))
      return () => {
        offTime(); offPlay(); offPause(); offFinish()
        ws.destroy()
        wsRef.current = null
      }
    }, [])

    const segmentsRef = useRef<PciPauseSegment[]>([])
    const onTimeUpdateRef = useRef(onTimeUpdate)
    useEffect(() => { segmentsRef.current = segments }, [segments])
    useEffect(() => { onTimeUpdateRef.current = onTimeUpdate }, [onTimeUpdate])

    // Fetch + decode the recording whenever it changes.
    useEffect(() => {
      let cancelled = false
      let objectUrl: string | null = null
      setError(null)
      setUrl(null)
      setLanes(0)
      void (async () => {
        try {
          const res = await fetch(`/api/recordings/${recordingId}/stream`, { credentials: 'include' })
          if (res.status === 403) throw new Error('You do not have permission to listen to recordings.')
          if (res.status === 404) throw new Error('This recording is not available.')
          if (!res.ok) throw new Error(`Could not load the recording (${res.status}).`)
          try {
            const seg = JSON.parse(res.headers.get('x-pci-pause-segments') ?? '[]') as unknown
            if (Array.isArray(seg)) setSegments(seg as PciPauseSegment[])
          } catch { /* malformed header is non-fatal */ }
          const blob = await res.blob()
          if (cancelled) return
          objectUrl = URL.createObjectURL(blob)
          setUrl(objectUrl)

          const ws = wsRef.current
          if (!ws) return
          // WaveSurfer decodes the blob itself — pass it the raw bytes so the
          // same bytes drive both the waveform and playback.
          await ws.loadBlob(blob)
          if (cancelled) return
          let decoded = ws.getDecodedData()
          if (decoded && decoded.numberOfChannels === 1) {
            // Mono telephone audio mixes both parties into one channel —
            // mirror it so the split view always shows Customer / Agent.
            const ch = decoded.getChannelData(0)
            await ws.loadBlob(blob, [ch, Float32Array.from(ch)], decoded.duration)
            if (cancelled) return
            decoded = ws.getDecodedData()
            setLanes(2)
          } else {
            setLanes(decoded?.numberOfChannels ?? 1)
          }
          setDuration(decoded?.duration ?? ws.getDuration())
        } catch (err) {
          if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load the recording.')
        }
      })()
      return () => {
        cancelled = true
        wsRef.current?.stop()
        if (objectUrl) URL.revokeObjectURL(objectUrl)
      }
    }, [recordingId])

    const play = useCallback((): void => { void wsRef.current?.play() }, [])
    const pause = useCallback((): void => { wsRef.current?.pause() }, [])
    const stop = useCallback((): void => { wsRef.current?.stop() }, [])
    const skip = useCallback((delta: number): void => { wsRef.current?.skip(delta) }, [])

    useImperativeHandle(ref, () => ({
      playFrom: (seconds: number) => {
        const ws = wsRef.current
        if (!ws) return
        ws.setTime(Math.max(0, Math.min(seconds, ws.getDuration())))
        void ws.play()
      },
    }))

    const loading = !error && lanes === 0
    const btn = 'inline-flex items-center gap-1.5 rounded-[3px] px-3 py-1.5 text-xs font-semibold shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50'
    const laneLabel = (i: number): string => channelLabels?.[i] ?? `Audio ${i + 1}`

    return (
      <section aria-label="Recording player" className="overflow-hidden rounded-[3px] border border-slate-200 bg-white shadow-sm">
        <div className="grid grid-cols-[9.5rem_1fr] gap-4 p-4">
          <div className="flex flex-col items-center justify-center gap-1">
            <Party label="Source" number={source} tone="blue" />
            <span className="my-1 h-6 w-px bg-gradient-to-b from-blue-200 to-emerald-200" />
            <Party label="Destination" number={destination} tone="emerald" />
          </div>

          <div className="relative min-w-0">
            {/* The WaveSurfer containers must stay mounted from the first
                render — the create-effect needs the ref targets — so loading
                and error states overlay them instead of replacing them. */}
            <div className={loading || error ? 'invisible' : ''}>
              <div className="relative rounded-[3px] border border-slate-100 bg-slate-50/70">
                <div ref={waveRef} className="w-full px-1" />
                <div className="pointer-events-none absolute inset-0">
                  {Array.from({ length: Math.min(lanes, 2) }).map((_, i) => (
                    <span
                      key={i}
                      className={`absolute left-2 rounded-[2px] px-1.5 text-[10px] font-semibold uppercase tracking-wide ${LANE_COLORS[i]?.chip ?? 'bg-slate-100 text-slate-500'}`}
                      style={{ top: `calc(${(i * 100) / Math.max(lanes, 1)}% + 3px)` }}
                    >
                      {laneLabel(i)}
                    </span>
                  ))}
                </div>
              </div>
              <div ref={timelineRef} className="px-1" />
            </div>
            {error ? (
              <div className="absolute inset-0 flex min-h-36 items-center justify-center gap-2 rounded-[3px] border border-slate-100 bg-slate-50/70 text-sm text-red-600">
                <AlertCircle className="h-4 w-4" /> {error}
              </div>
            ) : loading ? (
              <div className="absolute inset-0 flex min-h-36 items-center justify-center gap-2 rounded-[3px] border border-slate-100 bg-slate-50/70 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading recording…
              </div>
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/60 px-4 py-3">
          <div className="text-sm text-slate-600">
            Total call time : <b className="tabular-nums text-slate-900">{hhmmss(duration)}</b>
            <span className="ml-3 tabular-nums text-xs text-slate-400">{hhmmss(current)}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" className={`${btn} bg-emerald-600 text-white hover:bg-emerald-700`} onClick={play} disabled={!url || playing}>
              <Play className="h-3.5 w-3.5" /> Play
            </button>
            <button type="button" className={`${btn} bg-amber-500 text-white hover:bg-amber-600`} onClick={pause} disabled={!url || !playing}>
              <Pause className="h-3.5 w-3.5" /> Pause
            </button>
            <button type="button" className={`${btn} bg-red-600 text-white hover:bg-red-700`} onClick={stop} disabled={!url}>
              <Square className="h-3.5 w-3.5" /> Stop
            </button>
            <button type="button" className={`${btn} bg-blue-600 text-white hover:bg-blue-700`} onClick={() => skip(-SKIP_SECONDS)} disabled={!url}>
              <Rewind className="h-3.5 w-3.5" /> Backward
            </button>
            <button type="button" className={`${btn} bg-blue-600 text-white hover:bg-blue-700`} onClick={() => skip(SKIP_SECONDS)} disabled={!url}>
              <FastForward className="h-3.5 w-3.5" /> Forward
            </button>
            {url ? (
              <a href={url} download={fileName} className={`${btn} bg-slate-800 text-white hover:bg-slate-900`}>
                <Download className="h-3.5 w-3.5" /> Download
              </a>
            ) : (
              <button type="button" className={`${btn} bg-slate-800 text-white`} disabled>
                <Download className="h-3.5 w-3.5" /> Download
              </button>
            )}
          </div>
        </div>

        {segments.length > 0 && (
          <p className="border-t border-slate-100 px-4 py-1.5 text-[11px] text-slate-400">
            {segments.length} PCI pause segment{segments.length === 1 ? '' : 's'} skipped during playback.
          </p>
        )}
      </section>
    )
  },
)
